import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { ProjectToTemplatePayload } from 'api/project';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import {
  Drawer,
  DrawerContent,
} from 'components/organisms/task-pane/task-detail';
import { BodyShort01 } from 'components/styles';
import {
  createProjectTemplate,
  toggleProjectToTemplate,
} from 'data/templates/projects';
import { useCurrentProject } from 'hooks/use-current-project';
import { useTranslate } from 'hooks/use-translation';
import { useCreateProjectToTemplateMutation, useTemplatesQuery } from 'queries';
import { toastService } from '../toast';

const FlexWrapper = styled.div`
  display: flex;
  gap: var(--spacing-05);
`;

const StyledButton = styled(Button)`
  flex: 1;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 0;
  padding-bottom: 0 !important;
`;
const SecondaryTitle = styled.div`
  ${BodyShort01};
  color: var(--gray-50);
`;

interface DrawerProps {
  isDrawerVisible: boolean;
  hideDrawer: () => void;
}

export const ProjectsToTemplatesDrawer: FC<DrawerProps> = ({
  isDrawerVisible,
  hideDrawer,
}) => {
  const projectDetails = useCurrentProject();
  const dispatch = useDispatch();
  const history = useHistory();
  const [form] = Form.useForm();
  const translate = useTranslate();
  const untitled = translate('untitled');
  const title = translate(
    'project.settings.general_information.save_as_template',
  );

  const InputLabel = translate(
    'project.settings.save.template.modal.template.name',
  );

  const phasesText = translate(
    'project.settings.save.template.modal.options.phases',
  );
  const subTasksText = translate(
    'project.settings.save.template.modal.options.subtasks',
  );

  const attachmentsText = translate(
    'project.settings.save.template.modal.options.attachments',
  );

  const dependenciesText = translate(
    'project.settings.save.template.modal.options.dependencies',
  );

  const assignmentsText = translate(
    'project.settings.save.template.modal.options.assignments',
  );
  const taskFieldsText = translate(
    'project.settings.save.template.modal.options.task_fields',
  );
  const taskDescription = translate(
    'project.settings.save.template.modal.options.task_description',
  );
  const tasksText = translate(
    'project.settings.save.template.modal.options.tasks',
  );
  const phaseDatesText = translate(
    'project.settings.save.template.modal.options.phase_dates',
  );

  const includeText = translate(
    'project.settings.save.template.modal.options.title',
  );

  const inputPlaceholderText = translate(
    'project.settings.save.template.modal.input.placeholder',
  );
  const requiredErrorMessage = translate('input.required');

  const { data: templates } = useTemplatesQuery();

  const [projectFields, setProjectFields] = useState<ProjectToTemplatePayload>({
    templateName: projectDetails.projectName,
    descriptionFlag: true,
    attachmentFlag: true,
    assignmentsFlag: true,
    fieldsFlag: true,
    dependenciesFlag: true,
  });

  const handleFieldsChange = (event: CheckboxChangeEvent) => {
    setProjectFields((sections) => {
      const { name } = event.target;

      return {
        ...sections,
        [name as string]: !sections[name as string],
      };
    });
  };

  const { mutate, isLoading } = useCreateProjectToTemplateMutation(
    projectDetails.projectId,
  );

  const createTemplate = () => {
    mutate(projectFields, {
      onSuccess: (data) => {
        const { templateName: templateTitle, templateContent } = data;

        const templateName = templateTitle || untitled;

        dispatch(
          toggleProjectToTemplate({
            isProjectToTemplate: true,
          }),
        );
        dispatch(
          createProjectTemplate({
            templateName,
            templateContent,
          }),
        );
        history.push('/templates/projects/new');
      },
      onError() {
        toastService({
          title: translate('project.settings.save.template.modal.error.title'),
          message: translate(
            'project.settings.save.template.modal.error.message',
          ),
        }).error();
      },
    });
  };

  return (
    <Drawer visible={isDrawerVisible} onClose={hideDrawer}>
      <DrawerContent>
        <DrawerContent.Title>
          <div>{title}</div>
          <SecondaryTitle>{projectDetails.projectName}</SecondaryTitle>
        </DrawerContent.Title>

        <DrawerContent.Content>
          <Form
            form={form}
            layout="vertical"
            onFinish={createTemplate}
            initialValues={{ templateName: projectFields.templateName }}
          >
            <Form.Item
              label={InputLabel}
              name="templateName"
              rules={[
                { required: true, message: requiredErrorMessage },
                {
                  validator: (_, value) => {
                    const formattedValue = (value || '').toLowerCase().trim();

                    if (
                      templates?.find(
                        (template) =>
                          template.templateName?.toLowerCase?.()?.trim?.() ===
                          formattedValue,
                      )
                    ) {
                      return Promise.reject(
                        new Error(`${value} already exists`),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                onChange={(e) => {
                  const templateName = e.target.value;

                  setProjectFields((fields) => {
                    return {
                      ...fields,
                      templateName,
                    };
                  });
                }}
                value={projectFields.templateName}
                placeholder={inputPlaceholderText}
              />
            </Form.Item>
            <StyledFormItem label={includeText}>
              <Checkbox checked disabled>
                {phasesText}
              </Checkbox>
            </StyledFormItem>
            <StyledFormItem>
              <Checkbox checked disabled>
                {tasksText}
              </Checkbox>
            </StyledFormItem>
            <StyledFormItem>
              <Checkbox checked disabled>
                {phaseDatesText}
              </Checkbox>
            </StyledFormItem>
            <StyledFormItem>
              <Checkbox checked disabled>
                {subTasksText}
              </Checkbox>
            </StyledFormItem>
            <StyledFormItem>
              <Checkbox
                name="descriptionFlag"
                checked={projectFields.descriptionFlag}
                onChange={handleFieldsChange}
              >
                {taskDescription}
              </Checkbox>
            </StyledFormItem>
            <StyledFormItem>
              <Checkbox
                name="attachmentFlag"
                checked={projectFields.attachmentFlag}
                onChange={handleFieldsChange}
              >
                {attachmentsText}
              </Checkbox>
            </StyledFormItem>
            <StyledFormItem>
              <Checkbox
                name="assignmentsFlag"
                checked={projectFields.assignmentsFlag}
                onChange={handleFieldsChange}
              >
                {assignmentsText}
              </Checkbox>
            </StyledFormItem>
            <StyledFormItem>
              <Checkbox
                name="fieldsFlag"
                checked={projectFields.fieldsFlag}
                onChange={handleFieldsChange}
              >
                {taskFieldsText}
              </Checkbox>
            </StyledFormItem>
            <StyledFormItem>
              <Checkbox
                name="dependenciesFlag"
                checked={projectFields.dependenciesFlag}
                onChange={handleFieldsChange}
              >
                {dependenciesText}
              </Checkbox>
            </StyledFormItem>
          </Form>
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <FlexWrapper>
            <StyledButton onClick={hideDrawer}>
              {translate('cancel')}
            </StyledButton>
            <StyledButton
              onClick={() => form.submit()}
              type="primary"
              loading={isLoading}
            >
              {translate('project.settings.save.template.modal.create_button')}
            </StyledButton>
          </FlexWrapper>
        </DrawerContent.Footer>
      </DrawerContent>
    </Drawer>
  );
};
